import { useState, useRef, useEffect, useCallback } from 'react'
import store from 'store'
import { useAppDispatch, useAppSelector } from '../Hooks/redux'
import {
  startDubbingRequest,
  resetDubbingState,
  selectCurrentStep,
  selectDubbingError,
  selectLastCreatedJob,
} from '../Redux/DubbingRedux'

const LANGUAGES = [
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
]

const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024
const MAX_AUDIO_SIZE = 500 * 1024 * 1024 // 500MB for main audio upload
const MAX_VOICE_CLONE_SIZE = 50 * 1024 * 1024 // 50MB for voice clone
const MAX_MEDIA_DURATION = 30 * 60 // 30 minutes in seconds
const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
]
const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/mp4',
  'audio/x-m4a',
  'audio/ogg',
]

const CREDIT_PRICE = 0.3
const HANDLING_FEE = 0.5

export default function HomePage() {
  const mediaInputRef = useRef(null)
  const voiceCloneInputRef = useRef(null)
  const videoRef = useRef(null)
  const audioRef = useRef(null)

  // Media (video or audio) state
  const [mediaFile, setMediaFile] = useState(null)
  const [mediaPreview, setMediaPreview] = useState(null)
  const [mediaMetadata, setMediaMetadata] = useState(null)
  const [mediaError, setMediaError] = useState('')
  const [isMediaLoading, setIsMediaLoading] = useState(false)
  const [mediaType, setMediaType] = useState(null) // 'video' or 'audio'

  // Voice clone audio state
  const [voiceCloneFile, setVoiceCloneFile] = useState(null)
  const [voiceClonePreview, setVoiceClonePreview] = useState(null)
  const [voiceCloneDuration, setVoiceCloneDuration] = useState(null)
  const [voiceCloneError, setVoiceCloneError] = useState('')

  // Stock voices (MiniMax + ElevenLabs)
  const [stockVoices, setStockVoices] = useState([])
  const [voicesLoading, setVoicesLoading] = useState(false)
  const [playingVoiceId, setPlayingVoiceId] = useState(null)
  const voiceAudioRef = useRef(null)

  // Configuration state
  const [selectedLanguages, setSelectedLanguages] = useState([])
  const [voiceType, setVoiceType] = useState('stock')
  const [selectedVoice, setSelectedVoice] = useState(null)
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false)
  const [addSubtitles, setAddSubtitles] = useState(false)
  const [addLipSync, setAddLipSync] = useState(false)

  // UI state
  const [isDraggingMedia, setIsDraggingMedia] = useState(false)
  const [isDraggingVoiceClone, setIsDraggingVoiceClone] = useState(false)

  // Redux
  const dispatch = useAppDispatch()
  const currentStep = useAppSelector(selectCurrentStep)
  const dubbingError = useAppSelector(selectDubbingError)
  const lastCreatedJob = useAppSelector(selectLastCreatedJob)

  // Format helpers
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Media file validation (video or audio)
  const validateMediaFile = (file) => {
    if (!file) return { error: 'No file selected', type: null }

    const isVideo =
      ALLOWED_VIDEO_TYPES.includes(file.type) ||
      file.name.match(/\.(mp4|webm|mov|avi|mkv)$/i)
    const isAudio =
      ALLOWED_AUDIO_TYPES.includes(file.type) ||
      file.name.match(/\.(mp3|wav|m4a|ogg)$/i)

    if (!isVideo && !isAudio) {
      return {
        error:
          'Invalid file type. Please upload a video (MP4, WebM, MOV, AVI, MKV) or audio (MP3, WAV, M4A, OGG)',
        type: null,
      }
    }

    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      return {
        error: `Video too large. Maximum size is ${formatFileSize(MAX_VIDEO_SIZE)}`,
        type: null,
      }
    }

    if (isAudio && file.size > MAX_AUDIO_SIZE) {
      return {
        error: `Audio too large. Maximum size is ${formatFileSize(MAX_AUDIO_SIZE)}`,
        type: null,
      }
    }

    return { error: null, type: isVideo ? 'video' : 'audio' }
  }

  const handleMediaSelect = useCallback((file) => {
    setMediaError('')
    setMediaMetadata(null)
    setMediaType(null)

    const { error, type } = validateMediaFile(file)
    if (error) {
      setMediaError(error)
      return
    }

    setIsMediaLoading(true)
    setMediaFile(file)
    setMediaType(type)
    const url = URL.createObjectURL(file)
    setMediaPreview(url)

    if (type === 'audio') {
      const audio = new Audio(url)
      audio.addEventListener('loadedmetadata', () => {
        if (audio.duration > MAX_MEDIA_DURATION) {
          setMediaError('Audio duration exceeds 30 minutes. Please upload a shorter file.')
          setMediaFile(null)
          setMediaPreview(null)
          setMediaType(null)
          URL.revokeObjectURL(url)
          setIsMediaLoading(false)
          return
        }
        setMediaMetadata({ duration: audio.duration })
        setIsMediaLoading(false)
      })
      audio.addEventListener('error', () => {
        setMediaError(
          'Failed to load audio. The file may be corrupted or unsupported.',
        )
        setIsMediaLoading(false)
      })
    }
  }, [])

  const handleVideoLoaded = useCallback(() => {
    if (videoRef.current) {
      const video = videoRef.current
      if (video.duration > MAX_MEDIA_DURATION) {
        setMediaError('Video duration exceeds 30 minutes. Please upload a shorter file.')
        setMediaFile(null)
        if (mediaPreview) URL.revokeObjectURL(mediaPreview)
        setMediaPreview(null)
        setMediaType(null)
        setIsMediaLoading(false)
        return
      }
      setMediaMetadata({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      })
      setIsMediaLoading(false)
    }
  }, [mediaPreview])

  const handleVideoError = useCallback(() => {
    setMediaError(
      'Failed to load video. The file may be corrupted or unsupported.',
    )
    setIsMediaLoading(false)
  }, [])

  const clearMedia = useCallback(() => {
    if (mediaPreview) URL.revokeObjectURL(mediaPreview)
    setMediaFile(null)
    setMediaPreview(null)
    setMediaMetadata(null)
    setMediaError('')
    setMediaType(null)
    if (mediaInputRef.current) mediaInputRef.current.value = ''
  }, [mediaPreview])

  // Voice clone audio file handling
  const validateVoiceCloneFile = (file) => {
    if (!file) return 'No file selected'
    if (
      !ALLOWED_AUDIO_TYPES.includes(file.type) &&
      !file.name.match(/\.(mp3|wav|m4a|ogg)$/i)
    ) {
      return 'Invalid file type. Please upload MP3, WAV, M4A, or OGG'
    }
    if (file.size > MAX_VOICE_CLONE_SIZE) {
      return `File too large. Maximum size is ${formatFileSize(MAX_VOICE_CLONE_SIZE)}`
    }
    return null
  }

  const handleVoiceCloneSelect = useCallback((file) => {
    setVoiceCloneError('')
    setVoiceCloneDuration(null)
    const error = validateVoiceCloneFile(file)
    if (error) {
      setVoiceCloneError(error)
      return
    }
    setVoiceCloneFile(file)
    const url = URL.createObjectURL(file)
    setVoiceClonePreview(url)
    const audio = new Audio(url)
    audio.addEventListener('loadedmetadata', () =>
      setVoiceCloneDuration(audio.duration),
    )
    audio.addEventListener('error', () =>
      setVoiceCloneError('Failed to load audio file'),
    )
  }, [])

  const clearVoiceClone = useCallback(() => {
    if (voiceClonePreview) URL.revokeObjectURL(voiceClonePreview)
    setVoiceCloneFile(null)
    setVoiceClonePreview(null)
    setVoiceCloneDuration(null)
    setVoiceCloneError('')
    if (voiceCloneInputRef.current) voiceCloneInputRef.current.value = ''
  }, [voiceClonePreview])

  // Drag and drop handlers
  const handleMediaDrop = (e) => {
    e.preventDefault()
    setIsDraggingMedia(false)
    handleMediaSelect(e.dataTransfer.files[0])
  }
  const handleMediaDragOver = (e) => {
    e.preventDefault()
    setIsDraggingMedia(true)
  }
  const handleMediaDragLeave = (e) => {
    e.preventDefault()
    setIsDraggingMedia(false)
  }
  const handleVoiceCloneDrop = (e) => {
    e.preventDefault()
    setIsDraggingVoiceClone(false)
    handleVoiceCloneSelect(e.dataTransfer.files[0])
  }
  const handleVoiceCloneDragOver = (e) => {
    e.preventDefault()
    setIsDraggingVoiceClone(true)
  }
  const handleVoiceCloneDragLeave = (e) => {
    e.preventDefault()
    setIsDraggingVoiceClone(false)
  }

  // Language selection
  const toggleLanguage = (code) => {
    if (selectedLanguages.includes(code)) {
      setSelectedLanguages(selectedLanguages.filter((l) => l !== code))
    } else if (selectedLanguages.length < 10) {
      setSelectedLanguages([...selectedLanguages, code])
    }
  }

  // Cleanup URLs
  useEffect(() => {
    return () => {
      if (mediaPreview) URL.revokeObjectURL(mediaPreview)
      if (voiceClonePreview) URL.revokeObjectURL(voiceClonePreview)
    }
  }, [])

  // Fetch stock voices on mount (MiniMax first, then ElevenLabs)
  useEffect(() => {
    const fetchVoices = async () => {
      setVoicesLoading(true)
      try {
        const token = store.get('token')
        const response = await fetch(
          `${process.env.BASE_URL || '/api/v1'}/voices`,
          {
            headers: {
              Authorization: `Bearer ${token || ''}`,
            },
          },
        )
        if (response.ok) {
          const data = await response.json()
          setStockVoices(data.voices || [])
          if (data.voices?.length > 0 && !selectedVoice) {
            setSelectedVoice(data.voices[0].id)
          }
        }
      } catch (e) {
        console.error('Failed to fetch voices:', e)
      } finally {
        setVoicesLoading(false)
      }
    }
    fetchVoices()
  }, [])

  // Handle voice preview playback
  const handlePlayPreview = (voice) => {
    if (playingVoiceId === voice.id) {
      // Stop playing
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause()
        voiceAudioRef.current.currentTime = 0
      }
      setPlayingVoiceId(null)
    } else {
      // Play new voice
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause()
      }
      const audio = new Audio(voice.preview_url)
      audio.onended = () => setPlayingVoiceId(null)
      audio.onerror = () => setPlayingVoiceId(null)
      audio.play()
      voiceAudioRef.current = audio
      setPlayingVoiceId(voice.id)
    }
  }

  // Credit calculation - use actual duration in minutes (not rounded)
  const mediaDurationMinutes = mediaMetadata?.duration
    ? mediaMetadata.duration / 60
    : 1
  const languageCount = Math.max(selectedLanguages.length, 1)

  const calculateCredits = () => {
    const mins = mediaDurationMinutes
    const langs = languageCount

    // Base: 1 credit = 1 min × 1 language × stock voice
    let baseCredits = mins * langs

    // Voice clone: +1 credit/min
    let voiceCloneCredits = voiceType === 'clone' ? mins : 0

    // Subtitles: +0.2 credit/min
    let subtitleCredits = addSubtitles ? mins * 0.2 : 0

    // Lip-sync: +7 credits/min
    let lipSyncCredits = addLipSync ? mins * 7 : 0

    return {
      base: baseCredits,
      voiceClone: voiceCloneCredits,
      subtitles: subtitleCredits,
      lipSync: lipSyncCredits,
      total: baseCredits + voiceCloneCredits + subtitleCredits + lipSyncCredits,
    }
  }

  const credits = calculateCredits()
  const creditsCost = credits.total * CREDIT_PRICE
  const estimatedPrice = (creditsCost + HANDLING_FEE).toFixed(2)

  const canStartDubbing =
    mediaFile &&
    !mediaError &&
    selectedLanguages.length > 0 &&
    (voiceType === 'stock' || voiceType === 'clone')

  const isProcessing =
    currentStep === 'presigning' ||
    currentStep === 'uploading' ||
    currentStep === 'creating' ||
    currentStep === 'redirecting'

  const handleStartDubbing = () => {
    if (!canStartDubbing || isProcessing) return

    dispatch(
      startDubbingRequest({
        mediaFile,
        mediaType,
        config: {
          targetLanguages: selectedLanguages,
          voiceMode: voiceType,
          lipSync: addLipSync && mediaType === 'video',
          subtitles: addSubtitles,
          durationSeconds: mediaMetadata?.duration || 60,
        },
        callback: (data, error) => {
          if (error) {
            console.error('Dubbing failed:', error)
          } else {
            console.log('Dubbing job created:', data)
          }
        },
      }),
    )
  }

  const handleResetDubbing = () => {
    dispatch(resetDubbingState())
    clearMedia()
    setSelectedLanguages([])
  }

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* LEFT COLUMN: All Configurations */}
      <div className="flex-1 lg:w-[65%] space-y-6">
        {/* Media Upload Section (Video or Audio) */}
        <section className="bg-card border border-muted-bg rounded-[16px] p-6 shadow-card">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Upload Video or Audio
          </h2>

          {!mediaFile ? (
            <div
              onClick={() => mediaInputRef.current?.click()}
              onDrop={handleMediaDrop}
              onDragOver={handleMediaDragOver}
              onDragLeave={handleMediaDragLeave}
              className={`
                w-full h-[200px] border-2 border-dashed rounded-[12px] cursor-pointer
                flex flex-col items-center justify-center gap-3 transition-all
                ${
                  isDraggingMedia
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-bg bg-secondary/30 hover:bg-secondary hover:border-primary'
                }
              `}
            >
              <input
                ref={mediaInputRef}
                type="file"
                accept="video/*,audio/*"
                className="hidden"
                onChange={(e) => handleMediaSelect(e.target.files[0])}
              />
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-base font-medium text-foreground">
                  Drag & drop your video or audio here
                </p>
                <p className="text-sm text-muted">or click to browse</p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 text-xs text-muted">
                <span className="px-2 py-1 bg-secondary rounded">
                  Video: MP4, WebM, MOV (max 2GB)
                </span>
                <span className="px-2 py-1 bg-secondary rounded">
                  Audio: MP3, WAV, M4A (max 500MB)
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {mediaType === 'video' ? (
                <div className="relative rounded-[12px] overflow-hidden bg-black">
                  <video
                    ref={videoRef}
                    src={mediaPreview}
                    controls
                    className="w-full aspect-video"
                    onLoadedMetadata={handleVideoLoaded}
                    onError={handleVideoError}
                  />
                  {isMediaLoading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-[12px] p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-foreground">
                        Audio File
                      </p>
                      <p className="text-sm text-muted">
                        {mediaMetadata
                          ? formatDuration(mediaMetadata.duration)
                          : 'Loading...'}
                      </p>
                    </div>
                  </div>
                  <audio
                    ref={audioRef}
                    src={mediaPreview}
                    controls
                    className="w-full"
                  />
                </div>
              )}
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-[8px]">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0 ${mediaType === 'video' ? 'bg-primary/10' : 'bg-accent/10'}`}
                  >
                    {mediaType === 'video' ? (
                      <svg
                        className="w-4 h-4 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4 text-accent"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {mediaFile.name}
                    </p>
                    <p className="text-xs text-muted">
                      {formatFileSize(mediaFile.size)}
                      {mediaMetadata &&
                        ` • ${formatDuration(mediaMetadata.duration)}`}
                      {mediaType === 'video' &&
                        mediaMetadata?.width &&
                        ` • ${mediaMetadata.width}×${mediaMetadata.height}`}
                      <span
                        className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${mediaType === 'video' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}
                      >
                        {mediaType === 'video' ? 'VIDEO' : 'AUDIO'}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearMedia}
                  className="text-sm text-muted hover:text-destructive transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          {mediaError && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-[8px] flex items-center gap-2">
              <svg
                className="w-4 h-4 text-destructive flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-destructive">{mediaError}</p>
            </div>
          )}
        </section>

        {/* Language Selection */}
        <section className="bg-card border border-muted-bg rounded-[16px] p-6 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                  />
                </svg>
                Target Languages
              </h2>
              <p className="text-sm text-muted mt-1">
                Select up to 10 languages to dub your video into
              </p>
            </div>
            {selectedLanguages.length > 0 && (
              <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                {selectedLanguages.length} selected
              </span>
            )}
          </div>

          <div className="max-h-[240px] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {LANGUAGES.map((lang) => {
                const isSelected = selectedLanguages.includes(lang.code)
                return (
                  <button
                    key={lang.code}
                    onClick={() => toggleLanguage(lang.code)}
                    className={`
                      relative p-3 rounded-[12px] border-2 text-left transition-all
                      ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-muted-bg bg-card hover:border-primary/50 hover:bg-secondary/50'
                      }
                    `}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                    <p className="text-sm font-medium text-foreground">
                      {lang.name}
                    </p>
                    <p className="text-xs text-muted mt-0.5">{lang.native}</p>
                  </button>
                )
              })}
            </div>
          </div>
        </section>

        {/* Voice Settings */}
        <section className="bg-card border border-muted-bg rounded-[16px] p-6 shadow-card">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
            Voice Settings
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <label
              className={`flex items-start gap-3 p-4 border rounded-[12px] cursor-pointer transition-all ${voiceType === 'stock' ? 'border-primary bg-primary/5' : 'border-muted-bg hover:border-primary/50'}`}
            >
              <input
                type="radio"
                name="voiceType"
                checked={voiceType === 'stock'}
                onChange={() => setVoiceType('stock')}
                className="mt-0.5 w-4 h-4 text-primary focus:ring-primary"
              />
              <div>
                <p className="font-medium text-foreground">Stock Voices</p>
                <p className="text-xs text-muted mt-0.5">
                  Professional AI voices, no setup needed
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-4 border rounded-[12px] cursor-pointer transition-all ${voiceType === 'clone' ? 'border-primary bg-primary/5' : 'border-muted-bg hover:border-primary/50'}`}
            >
              <input
                type="radio"
                name="voiceType"
                checked={voiceType === 'clone'}
                onChange={() => setVoiceType('clone')}
                className="mt-0.5 w-4 h-4 text-primary focus:ring-primary"
              />
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground">Voice Clone</p>
                  <span className="px-1.5 py-0.5 bg-accent text-white text-[10px] font-medium rounded">
                    +1 cr/min
                  </span>
                </div>
                <p className="text-xs text-muted mt-0.5">
                  Clone the voice from your uploaded file
                </p>
              </div>
            </label>
          </div>

          {voiceType === 'stock' && (
            <div className="relative">
              <label className="text-sm font-medium text-foreground mb-2 block">
                Select Voice
              </label>
              {voicesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-muted">
                    Loading voices...
                  </span>
                </div>
              ) : stockVoices.length === 0 ? (
                <p className="text-sm text-muted py-4 text-center">
                  No voices available
                </p>
              ) : (
                <>
                  {/* Selected voice / dropdown trigger */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setVoiceDropdownOpen(!voiceDropdownOpen)}
                      className="flex-1 flex items-center justify-between px-4 py-3 border-2 border-muted-bg rounded-[12px] bg-card hover:border-primary/50 transition-all text-left"
                    >
                      <div className="min-w-0">
                        {(() => {
                          const v = stockVoices.find(
                            (v) => v.id === selectedVoice,
                          )
                          if (!v)
                            return (
                              <span className="text-muted">
                                Choose a voice...
                              </span>
                            )
                          return (
                            <>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-foreground truncate">
                                  {v.name}
                                </p>
                                {/* <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                  Kokoro
                                </span> */}
                              </div>
                              <p className="text-xs text-muted mt-0.5">
                                {[
                                  v.labels?.language,
                                  v.labels?.gender,
                                  v.labels?.accent,
                                  v.labels?.age,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            </>
                          )
                        })()}
                      </div>
                      <svg
                        className={`w-5 h-5 text-muted flex-shrink-0 transition-transform ${voiceDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Preview button for selected voice */}
                    {(() => {
                      const v = stockVoices.find((v) => v.id === selectedVoice)
                      if (!v?.preview_url) return null
                      return (
                        <button
                          type="button"
                          onClick={() => handlePlayPreview(v)}
                          className={`flex items-center gap-1.5 px-4 py-3 rounded-[12px] border-2 text-sm font-medium transition-all whitespace-nowrap ${
                            playingVoiceId === v.id
                              ? 'border-primary bg-primary text-white'
                              : 'border-muted-bg bg-card text-foreground hover:border-primary/50'
                          }`}
                        >
                          {playingVoiceId === v.id ? (
                            <>
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Stop
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Preview
                            </>
                          )}
                        </button>
                      )
                    })()}
                  </div>

                  {/* Dropdown list */}
                  {voiceDropdownOpen && (
                    <div className="mt-2 border-2 border-muted-bg rounded-[12px] bg-card overflow-hidden shadow-lg">
                      <div className="max-h-[280px] overflow-y-auto divide-y divide-muted-bg/50">
                        {stockVoices.map((voice) => (
                          <button
                            key={voice.id}
                            type="button"
                            onClick={() => {
                              setSelectedVoice(voice.id)
                              setVoiceDropdownOpen(false)
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-secondary/50 transition-colors ${
                              selectedVoice === voice.id ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-foreground truncate">
                                    {voice.name}
                                  </p>
                                  {/* <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                    Kokoro
                                  </span> */}
                                </div>
                                <p className="text-xs text-muted mt-0.5">
                                  {[
                                    voice.labels?.language,
                                    voice.labels?.gender,
                                    voice.labels?.accent,
                                    voice.labels?.age,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                                {voice.description && (
                                  <p className="text-xs text-muted mt-0.5 truncate">
                                    {voice.description}
                                  </p>
                                )}
                              </div>
                              {voice.preview_url && (
                                <span
                                  role="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handlePlayPreview(voice)
                                  }}
                                  className={`ml-3 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                    playingVoiceId === voice.id
                                      ? 'bg-primary text-white'
                                      : 'bg-secondary text-foreground hover:bg-muted-bg'
                                  }`}
                                >
                                  {playingVoiceId === voice.id ? (
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {voiceType === 'clone' && (
            <div className="bg-secondary/50 border border-muted-bg rounded-[12px] p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[6px] bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Voice will be cloned from your uploaded file</p>
                  <p className="text-xs text-muted mt-0.5">The speaker's voice in your audio/video will be used for all dubbed languages</p>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Additional Options */}
        <section className="bg-card border border-muted-bg rounded-[16px] p-6 shadow-card">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
            Additional Options
          </h2>

          <div className="space-y-4">
            {/* Subtitles */}
            <label
              className={`flex items-center justify-between p-4 border rounded-[12px] cursor-pointer transition-all ${addSubtitles ? 'border-primary bg-primary/5' : 'border-muted-bg hover:border-primary/50'}`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={addSubtitles}
                  onChange={(e) => setAddSubtitles(e.target.checked)}
                  className="w-4 h-4 rounded border-muted-bg text-primary focus:ring-primary"
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Add Subtitles
                  </p>
                  <p className="text-xs text-muted">
                    Generate subtitles for all languages
                  </p>
                </div>
              </div>
              <span className="text-xs text-primary font-medium">
                +0.2 cr/min
              </span>
            </label>

            {/* Lip Sync - only available for video */}
            <label
              className={`flex items-center justify-between p-4 border rounded-[12px] transition-all ${
                mediaType === 'audio'
                  ? 'opacity-50 cursor-not-allowed border-muted-bg'
                  : addLipSync
                    ? 'border-primary bg-primary/5 cursor-pointer'
                    : 'border-muted-bg hover:border-primary/50 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={addLipSync && mediaType !== 'audio'}
                  onChange={(e) =>
                    mediaType !== 'audio' && setAddLipSync(e.target.checked)
                  }
                  disabled={mediaType === 'audio'}
                  className="w-4 h-4 rounded border-muted-bg text-primary focus:ring-primary"
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Lip-Sync
                  </p>
                  <p className="text-xs text-muted">
                    {mediaType === 'audio'
                      ? 'Only available for video files'
                      : "Sync speaker's lips to dubbed audio"}
                  </p>
                </div>
              </div>
              <span className="text-xs text-primary font-medium">
                +7 cr/min
              </span>
            </label>
          </div>
        </section>
      </div>

      {/* RIGHT COLUMN: Pricing Calculator (Fixed) */}
      <div className="lg:w-[35%]">
        <div className="lg:sticky lg:top-24">
          <div className="bg-card border border-muted-bg rounded-[16px] shadow-card overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary-light p-4">
              <h2 className="text-lg font-bold text-white">Cost Breakdown</h2>
            </div>

            {/* Credit Rules */}
            <div className="p-4 bg-secondary/50 border-b border-muted-bg">
              <p className="text-xs font-semibold text-foreground mb-2">
                Credit Rules:
              </p>
              <ul className="space-y-1 text-xs text-muted">
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted"></span>1
                  credit = 1 min × 1 language × stock voice
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted"></span>
                  Voice clone = +1 credit/min
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted"></span>
                  Subtitles = +0.2 credit/min
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted"></span>
                  Lip-sync = +7 credits/min
                </li>
                <li className="flex items-center gap-2 pt-1 border-t border-muted-bg mt-2">
                  <span className="w-1 h-1 rounded-full bg-primary"></span>
                  <span className="text-foreground font-medium">
                    1 credit = ${CREDIT_PRICE.toFixed(2)}
                  </span>
                </li>
              </ul>
            </div>

            {/* Cost Breakdown */}
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted">
                  Base ({formatDuration(mediaMetadata?.duration || 60)} ×{' '}
                  {languageCount} lang)
                </span>
                <span className="font-medium text-foreground">
                  {credits.base.toFixed(2)} credits
                </span>
              </div>

              {voiceType === 'clone' && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted">Voice Clone</span>
                  <span className="font-medium text-foreground">
                    +{credits.voiceClone.toFixed(2)} credits
                  </span>
                </div>
              )}

              {addSubtitles && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted">Subtitles</span>
                  <span className="font-medium text-foreground">
                    +{credits.subtitles.toFixed(2)} credits
                  </span>
                </div>
              )}

              {addLipSync && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted">Lip-Sync</span>
                  <span className="font-medium text-foreground">
                    +{credits.lipSync.toFixed(2)} credits
                  </span>
                </div>
              )}

              <div className="border-t border-muted-bg pt-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-foreground">
                    Total Credits
                  </span>
                  <span className="text-lg font-bold text-foreground">
                    {credits.total.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted">
                    Credits Cost ({credits.total.toFixed(2)} × ${CREDIT_PRICE.toFixed(2)})
                  </span>
                  <span className="font-medium text-foreground">
                    ${creditsCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted">Handling Fee</span>
                  <span className="font-medium text-foreground">
                    +${HANDLING_FEE.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="bg-primary/10 rounded-[12px] p-4 text-center">
                <p className="text-xs text-muted mb-1">Total Price</p>
                <p className="text-3xl font-bold text-primary">
                  ${estimatedPrice}
                </p>
              </div>
            </div>

            {/* Action Button */}
            <div className="p-4 border-t border-muted-bg">
              {currentStep === 'done' && lastCreatedJob ? (
                <div className="space-y-3">
                  <div className="bg-primary/10 border border-primary/20 rounded-[12px] p-4 text-center">
                    <svg
                      className="w-8 h-8 text-primary mx-auto mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="text-sm font-semibold text-foreground">
                      Dubbing Job Created!
                    </p>
                    <p className="text-xs text-muted mt-1">
                      Job #{lastCreatedJob.dubbing_job?.id} is now processing
                    </p>
                    <p className="text-xs text-muted">
                      Remaining credits: {lastCreatedJob.remaining_credits}
                    </p>
                  </div>
                  <button
                    onClick={handleResetDubbing}
                    className="w-full py-3 px-6 rounded-[12px] font-semibold bg-secondary text-foreground hover:bg-muted-bg transition-all"
                  >
                    Start New Dubbing
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleStartDubbing}
                    disabled={!canStartDubbing || isProcessing}
                    className={`w-full py-3 px-6 rounded-[12px] font-semibold flex items-center justify-center gap-2 transition-all ${
                      isProcessing
                        ? 'bg-primary/70 text-white cursor-wait'
                        : canStartDubbing
                          ? 'bg-primary text-white hover:bg-primary-hover'
                          : 'bg-muted-bg text-muted cursor-not-allowed'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {currentStep === 'presigning' && 'Preparing upload...'}
                        {currentStep === 'uploading' && 'Uploading file...'}
                        {currentStep === 'creating' && 'Creating job...'}
                        {currentStep === 'redirecting' && 'Redirecting to payment...'}
                      </>
                    ) : (
                      <>
                        {`Pay $${estimatedPrice} & Start Dubbing`}
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </>
                    )}
                  </button>

                  {dubbingError && (
                    <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-[8px]">
                      <p className="text-xs text-destructive text-center">
                        {dubbingError}
                      </p>
                    </div>
                  )}

                  {!canStartDubbing && !isProcessing && (
                    <p className="text-xs text-muted text-center mt-2">
                      {!mediaFile
                        ? 'Upload a video or audio to continue'
                        : selectedLanguages.length === 0
                          ? 'Select at least one language'
                          : voiceType === 'clone' && !voiceCloneFile
                            ? 'Upload voice sample for cloning'
                            : ''}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
